import React from 'react';
import LegalLayout from './LegalLayout';

const Privacy = () => (
  <LegalLayout title="Politique de confidentialité" slug="privacy" lastUpdated="27 octobre 2026">
    <p>
      OpenSapiens accorde une importance primordiale à la protection de vos données personnelles. La
      présente politique de confidentialité explique quelles données nous collectons, pourquoi, et
      comment elles sont traitées, dans le respect du <strong>RGPD</strong> (Règlement européen
      2016/679) et de la <strong>LPD</strong> (Loi fédérale suisse sur la protection des données).
    </p>

    <h2>1. Responsable du traitement</h2>
    <p>
      Le responsable du traitement des données est :
    </p>
    <ul>
      <li><strong>Responsable :</strong> OpenSapiens — propriétaire des domaines opensapiens.ch et opensapiens.io</li>
      <li><strong>Adresse :</strong> Genève, Suisse</li>
      <li><strong>Email :</strong> <a href="mailto:info@opensapiens.ch">info@opensapiens.ch</a></li>
    </ul>

    <h2>2. Données collectées</h2>
    <h3>2.1 Données fournies par l'Utilisateur</h3>
    <ul>
      <li><strong>Identification :</strong> nom d'usage, email, mot de passe (haché), avatar.</li>
      <li><strong>Profil :</strong> bio, expériences, formations, compétences, langues, centres d'intérêt.</li>
      <li><strong>Contenus :</strong> publications, messages privés, commentaires, sessions proposées.</li>
      <li><strong>Préférences :</strong> langue, fuseau horaire, préférences de notification.</li>
    </ul>
    <h3>2.2 Données collectées automatiquement</h3>
    <ul>
      <li>Données de connexion (adresse IP, identifiant de session, date et heure des connexions) ;</li>
      <li>Données techniques (navigateur, système d'exploitation, type d'appareil) ;</li>
      <li>Données d'usage (pages consultées, fonctionnalités utilisées).</li>
    </ul>
    <p>
      Aucune donnée bancaire n'est collectée pendant la phase Beta (les paiements sont désactivés).
      Lors de l'activation des paiements, les données bancaires seront traitées exclusivement par
      notre prestataire Stripe et ne transiteront jamais par nos serveurs.
    </p>

    <h2>3. Finalités du traitement</h2>
    <p>Vos données sont traitées pour les finalités suivantes :</p>
    <ul>
      <li>Création et gestion de votre compte (base légale : exécution du contrat) ;</li>
      <li>Mise en relation avec d'autres Utilisateurs (base légale : exécution du contrat) ;</li>
      <li>Envoi d'emails transactionnels (confirmation, réinitialisation de mot de passe) (base : exécution du contrat) ;</li>
      <li>Communication relative au service (notifications de réservation) (base : exécution du contrat) ;</li>
      <li>Amélioration et sécurité de la Plateforme (base : intérêt légitime) ;</li>
      <li>Respect de nos obligations légales (base : obligation légale).</li>
    </ul>

    <h2>4. Destinataires des données</h2>
    <p>
      Vos données sont accessibles aux seules personnes habilitées de l'éditeur, ainsi qu'aux
      sous-traitants techniques strictement nécessaires au fonctionnement du service :
    </p>
    <ul>
      <li><strong>Resend</strong> (envoi d'emails transactionnels) — serveurs UE/US, certifié DPF ;</li>
      <li><strong>MongoDB Atlas</strong> (hébergement de la base de données) — région UE ;</li>
      <li><strong>Emergent / Cloudflare</strong> (hébergement et CDN) ;</li>
      <li><strong>Jitsi Meet</strong> (visioconférence) — communications chiffrées peer-to-peer, aucun enregistrement côté serveur ;</li>
      <li><strong>OpenAI / Anthropic / Google</strong> (assistant IA, à l'initiative de l'Utilisateur uniquement) ;</li>
      <li><strong>Stripe</strong> (paiements — désactivé pendant la phase Beta).</li>
    </ul>
    <p>
      Aucune donnée n'est vendue ni cédée à des tiers à des fins commerciales.
    </p>

    <h2>5. Transferts hors UE/Suisse</h2>
    <p>
      Certains de nos sous-traitants peuvent héberger des données aux États-Unis. Dans ce cas, le
      transfert est encadré par les clauses contractuelles types de la Commission européenne ou par
      l'adhésion au Data Privacy Framework (DPF).
    </p>

    <h2>6. Durée de conservation</h2>
    <ul>
      <li><strong>Compte actif :</strong> tant que vous utilisez la Plateforme.</li>
      <li><strong>Compte supprimé :</strong> suppression immédiate des données personnelles ; les données comptables et transactionnelles sont conservées sous forme anonymisée pour la durée légale (10 ans en droit suisse).</li>
      <li><strong>Logs techniques :</strong> 12 mois maximum.</li>
      <li><strong>Données bancaires :</strong> conservées par Stripe selon ses propres durées de conservation (l'Éditeur n'en stocke pas de copie).</li>
    </ul>

    <h2>7. Vos droits</h2>
    <p>
      Conformément au RGPD et à la LPD, vous disposez des droits suivants :
    </p>
    <ul>
      <li><strong>Droit d'accès :</strong> consulter vos données depuis votre profil.</li>
      <li><strong>Droit de rectification :</strong> corriger vos données depuis votre profil ou la page Paramètres.</li>
      <li><strong>Droit à l'effacement (« droit à l'oubli ») :</strong> supprimer votre compte définitivement depuis la page Paramètres &gt; Zone de danger.</li>
      <li><strong>Droit à la portabilité :</strong> demander un export de vos données par email à <a href="mailto:info@opensapiens.ch">info@opensapiens.ch</a>.</li>
      <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données pour les finalités d'intérêt légitime.</li>
      <li><strong>Droit de limitation :</strong> demander la limitation du traitement.</li>
      <li><strong>Droit de retirer votre consentement</strong> à tout moment (notifications, communications optionnelles).</li>
    </ul>
    <p>
      Pour exercer vos droits, écrivez à <a href="mailto:info@opensapiens.ch">info@opensapiens.ch</a>.
      Nous répondons sous 30 jours.
    </p>

    <h2>8. Réclamations</h2>
    <p>
      Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation
      auprès du <strong>Préposé fédéral à la protection des données et à la transparence (PFPDT)</strong>
      en Suisse (<a href="https://www.edoeb.admin.ch" target="_blank" rel="noopener noreferrer">edoeb.admin.ch</a>),
      ou auprès de l'autorité de contrôle de votre État membre de résidence dans l'UE.
    </p>

    <h2>9. Cookies</h2>
    <p>
      OpenSapiens utilise uniquement des cookies <strong>strictement nécessaires</strong> au
      fonctionnement du service (session d'authentification, préférences). Aucun cookie de pistage
      publicitaire ou de mesure tierce n'est déposé.
    </p>

    <h2>10. Sécurité</h2>
    <p>
      Nous mettons en œuvre des mesures techniques et organisationnelles raisonnables pour protéger
      vos données : chiffrement TLS en transit, hachage bcrypt des mots de passe, accès aux données
      par identifiant unique, journalisation des accès administrateurs.
    </p>

    <h2>11. Modifications</h2>
    <p>
      Cette politique peut évoluer. Les modifications substantielles seront communiquées par email
      et/ou via une notification dans l'application au moins 15 jours avant leur entrée en vigueur.
    </p>

    <h2>12. Contact</h2>
    <p>
      Pour toute question relative au traitement de vos données :
      <a href="mailto:info@opensapiens.ch"> info@opensapiens.ch</a>
    </p>
  </LegalLayout>
);

export default Privacy;
